import React, { useState, useEffect } from 'react';
import styles from './AnalysisDetail.module.css';
import { Button } from '@/presentation/shared/components/Button/Button';
import { AnalysisEditor } from './AnalysisEditor';
import type { TimeframeInput } from '@/presentation/analysis/types';
// import { ConfluenceWizard } from '@/presentation/confluenceWizard/ConfluenceWizard';

type Timeframe = 'monthly' | 'weekly' | 'daily' | '4h' | '2h' | '1h' | '15min';

export type TimeframeAnalysis = {
  timeframe: Timeframe;
  tradingViewLink?: string;
  note?: string;
};

import type { AnalysisSetup } from './setups';
export type AnalysisDTO = {
  id: string;
  symbol: string;
  createdAt: string;
  updatedAt?: string;
  timeframes: Record<Timeframe, TimeframeAnalysis>;
  notes?: string;
  market?: 'Forex' | 'Crypto' | { value: string };
  setups?: AnalysisSetup[];
};

type Props = {
  analysis: AnalysisDTO;
  compactView?: boolean;
  onSave?: (updated: AnalysisDTO) => Promise<void> | void;
  // optional field name to auto-start editing and focus that field
  startEditingField?: string;
  onRequestDelete?: (id: string) => void;
  showSymbolTitle?: boolean;
};

export function AnalysisDetail({
  analysis,
  compactView = false,
  onSave,
  startEditingField,
  showSymbolTitle = true,
}: Props) {
  const [hasLinkedTrade, setHasLinkedTrade] = useState(false);
  // ConfluenceWizard entfernt
  useEffect(() => {
    // Check for linked trade in localStorage
    try {
      const raw = localStorage.getItem('mt_trades_v1');
      if (raw) {
        const trades = JSON.parse(raw);
        setHasLinkedTrade(
          Array.isArray(trades) &&
            trades.some((t) => t.analysisId === analysis.id && t.symbol === analysis.symbol)
        );
      } else {
        setHasLinkedTrade(false);
      }
    } catch {
      setHasLinkedTrade(false);
    }
  }, [analysis.id, analysis.symbol]);
  const normalizeMarket = (
    m?: 'Forex' | 'Crypto' | { value: string }
  ): 'Forex' | 'Crypto' | undefined => {
    if (!m) return undefined;
    if (
      typeof m === 'object' &&
      m &&
      'value' in m &&
      typeof (m as Record<string, unknown>).value === 'string'
    ) {
      const v = (m as Record<string, unknown>).value as string;
      return v === 'Forex' || v === 'Crypto' ? (v as 'Forex' | 'Crypto') : undefined;
    }
    if (m === 'Forex' || m === 'Crypto') return m;
    return undefined;
  };
  // const [hasLinkedTrade, setHasLinkedTrade] = useState(false); // entfernt, da nicht mehr benötigt
  const [editing, setEditing] = useState(false);
  const [localFocusField, setLocalFocusField] = useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (startEditingField) {
      setEditing(true);
    }
  }, [startEditingField]);

  // useEffect für hasLinkedTrade entfernt
  return (
    <div className={styles.container} data-testid="analysis-detail">
      {/* ConfluenceWizard entfernt */}

      <div className={styles.header}>
        {showSymbolTitle ? (
          <h2
            className={styles.symbol}
            onClick={() => {
              setEditing(true);
              setLocalFocusField('symbol');
              setTimeout(() => setLocalFocusField(undefined), 200);
            }}
            role="button"
            aria-label={`Edit analysis ${analysis.symbol}`}
            style={{ cursor: 'pointer' }}
          >
            {analysis.symbol}
          </h2>
        ) : null}
        {/* Action-Dropdown entfernt, keine Aktionen mehr hier */}
      </div>

      {!editing ? (
        <>
          {hasLinkedTrade && (
            <Button
              type="button"
              onClick={() => {
                window.location.hash = '#/journal';
                setTimeout(() => {
                  window.dispatchEvent(new Event('hashchange'));
                }, 10);
              }}
              aria-label={`Open trade for ${analysis.symbol}`}
            >
              Open trade for {analysis.symbol}
            </Button>
          )}
          <div className={styles.metaBlock}>
            <div>
              <strong>Market:</strong> {normalizeMarket(analysis.market) ?? '-'}
            </div>
            <div>
              <strong>Created:</strong>{' '}
              {analysis.createdAt ? new Date(analysis.createdAt).toLocaleString() : '-'}
            </div>
            {analysis.updatedAt && (
              <div>
                <strong>Updated:</strong> {new Date(analysis.updatedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className={styles.setupsBlock}>
            <div>
              <strong>Setups:</strong>
            </div>
            <ul className={styles.setupsList}>
              {(analysis.setups ?? []).map((setup: AnalysisSetup, i: number) => (
                <li key={i} className={setup.key === 'fib-level' ? styles.fibTag : undefined}>
                  {setup.key === 'fib-level' ? `Fib ${setup.value}` : setup.key}
                </li>
              ))}
            </ul>
          </div>
          <div
            className={styles.notes}
            onClick={() => {
              setEditing(true);
              setLocalFocusField('notes');
              setTimeout(() => setLocalFocusField(undefined), 200);
            }}
            role="button"
            aria-label="Edit notes"
            style={{ cursor: 'pointer' }}
          >
            {analysis.notes}
          </div>

          <div className={styles.timeframes}>
            {Object.values(analysis.timeframes).map((tf) => (
              <div
                key={tf.timeframe}
                className={`${styles.tf} ${compactView ? styles.compact : ''}`}
              >
                <div className={styles.tfHeader}>
                  <strong>{tf.timeframe.toUpperCase()}</strong>
                  {tf.tradingViewLink ? (
                    <a
                      href={tf.tradingViewLink}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.link}
                      aria-label="Open TradingView link"
                      title="Open in TradingView"
                    >
                      <svg
                        className={styles.tvIcon}
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h6v2H7v2h10v-2h-2v-2h6c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM21 17H3V5h18v12zM9 21h6v-2H9v2z"
                          fill="currentColor"
                        />
                      </svg>
                    </a>
                  ) : null}
                </div>
                {tf.note ? (
                  <p
                    className={styles.tfNote}
                    onClick={() => {
                      setEditing(true);
                      setLocalFocusField(`tf:${tf.timeframe}:note`);
                      setTimeout(() => setLocalFocusField(undefined), 200);
                    }}
                    role="button"
                    aria-label={`Edit ${tf.timeframe} note`}
                    style={{ cursor: 'pointer' }}
                  >
                    {tf.note}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ paddingTop: 8 }}>
          <AnalysisEditor
            initial={{
              symbol: analysis.symbol,
              notes: analysis.notes,
              market: normalizeMarket(
                (analysis as unknown as { market?: 'Forex' | 'Crypto' | { value: string } }).market
              ),
              timeframes: Object.values(analysis.timeframes).reduce(
                (acc: Record<string, TimeframeAnalysis>, tf) => ({
                  ...acc,
                  [tf.timeframe]: tf,
                }),
                {}
              ),
            }}
            // pass focus request down so editor can focus a specific field
            focusField={startEditingField ?? localFocusField}
            onSave={async (values) => {
              // map timeframes array back to record keyed by timeframe
              const tfRecord: Record<Timeframe, TimeframeAnalysis> = (
                values.timeframes || []
              ).reduce(
                (acc: Record<string, TimeframeAnalysis>, t: TimeframeInput) => ({
                  ...acc,
                  [t.timeframe]: {
                    timeframe: t.timeframe,
                    tradingViewLink: t.tradingViewLink,
                    note: t.note,
                  },
                }),
                {}
              ) as Record<Timeframe, TimeframeAnalysis>;

              const updated: AnalysisDTO = {
                ...analysis,
                symbol: values.symbol ?? analysis.symbol,
                notes: values.notes ?? analysis.notes,
                timeframes: Object.keys(tfRecord).length
                  ? (tfRecord as Record<Timeframe, TimeframeAnalysis>)
                  : analysis.timeframes,
                updatedAt: new Date().toISOString(),
                market:
                  (values.market as 'Forex' | 'Crypto') ??
                  normalizeMarket(
                    (analysis as unknown as { market?: 'Forex' | 'Crypto' | { value: string } })
                      .market
                  ),
              };
              try {
                if (onSave) await onSave(updated);
              } catch {
                /* ignore */
              }
              setEditing(false);
            }}
          />
          <div style={{ marginTop: 8 }}>
            <Button variant="ghost" onClick={() => setEditing(false)} style={{ marginRight: 8 }}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalysisDetail;
